import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { Activity, Loader2, Frown } from "lucide-react";
import { motion } from "framer-motion";
import { API, useAuth } from "@/App";

const SymptomTrends = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/wellness/symptom-trends?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token, days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  const hasData = data && data.total_logs > 0;

  return (
    <div className="space-y-6" data-testid="symptom-trends-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Symptom <span className="gradient-text">Trends</span>
        </h1>
        <p className="text-mystic-200/70">Patterns in your body across the last {days} days</p>
      </motion.div>

      <div className="flex justify-center gap-2">
        {[30, 60, 90, 180].map(n => (
          <button
            key={n}
            onClick={() => setDays(n)}
            data-testid={`trends-range-${n}-btn`}
            className={`px-4 py-1.5 rounded-full text-sm transition-all ${
              days === n ? "bg-mystic-500 text-white" : "bg-mystic-800/50 text-mystic-200 border border-white/10 hover:border-mystic-400/40"
            }`}
          >
            {n} days
          </button>
        ))}
      </div>

      {!hasData ? (
        <Card className="glass">
          <CardContent className="py-14 text-center">
            <Frown className="w-10 h-10 text-mystic-400 mx-auto mb-3" />
            <p className="text-white mb-1">No symptom data yet</p>
            <p className="text-mystic-200/70 text-sm">
              Log a few days with symptoms in the Calendar and your trends will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Symptoms Bar */}
          <Card className="glass" data-testid="top-symptoms-chart">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-mystic-300" />
                Most Frequent Symptoms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.top_symptoms} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                  <XAxis type="number" stroke="#c4a0ff" fontSize={12} />
                  <YAxis type="category" dataKey="symptom" stroke="#c4a0ff" fontSize={12} width={100} />
                  <Tooltip
                    contentStyle={{ background: "#1a0d2e", border: "1px solid #3b1f6e", borderRadius: 8, color: "#fff" }}
                  />
                  <Bar dataKey="count" fill="#9d4edd" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Daily Timeline */}
          <Card className="glass" data-testid="daily-timeline-chart">
            <CardHeader>
              <CardTitle className="text-white">Daily Symptom Load</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                  <XAxis dataKey="date" stroke="#c4a0ff" fontSize={10} tick={{ dy: 4 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#c4a0ff" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#1a0d2e", border: "1px solid #3b1f6e", borderRadius: 8, color: "#fff" }} />
                  <Line type="monotone" dataKey="symptom_count" stroke="#f472b6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Mood distribution */}
          <Card className="glass lg:col-span-2" data-testid="mood-distribution-chart">
            <CardHeader>
              <CardTitle className="text-white">Mood Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.mood_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                  <XAxis dataKey="mood" stroke="#c4a0ff" fontSize={12} />
                  <YAxis stroke="#c4a0ff" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#1a0d2e", border: "1px solid #3b1f6e", borderRadius: 8, color: "#fff" }} />
                  <Legend wrapperStyle={{ color: "#c4a0ff" }} />
                  <Bar dataKey="count" fill="#fbbf24" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SymptomTrends;
